import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FolderKanban, Users, Plus, Shield, Archive } from 'lucide-react';
import { format } from 'date-fns';
import './ProjectList.css';
import { absoluteMediaUrl, useMediaToken } from '../media';
import UserAvatar from './UserAvatar';
import CreateProjectModal from './CreateProjectModal';

function ProjectList({ projects, onCreateProject }) {
  const [view, setView] = useState('active');   // 'active' | 'archived'
  const [archived, setArchived] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // The card header background is a signed /uploads URL built at render
  // time. Subscribe so the cards re-render when the media token rotates,
  // instead of holding a URL signed with an expired one.
  useMediaToken();

  const fetchArchived = useCallback(async () => {
    setLoadingArchived(true);
    try {
      const res = await axios.get('/projects?archivedOnly=true');
      setArchived(res.data || []);
    } catch (e) {
      setArchived([]);
    } finally {
      setLoadingArchived(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'archived') fetchArchived();
  }, [view, fetchArchived]);

  const displayed = view === 'archived' ? archived : projects;

  return (
    <div className="project-list-container">
      <div className="project-list-header">
        <div>
          <h1>Your projects</h1>
          <p>Manage and collaborate on your team's work</p>
        </div>
        <div className="project-list-actions">
          <div className="project-list-view-toggle">
            <button
              className={`pl-view-btn ${view === 'active' ? 'is-active' : ''}`}
              onClick={() => setView('active')}
            >
              Active <span className="pl-view-count">{projects.length}</span>
            </button>
            <button
              className={`pl-view-btn ${view === 'archived' ? 'is-active' : ''}`}
              onClick={() => setView('archived')}
            >
              <Archive size={12} /> Archived
              {view === 'archived' && <span className="pl-view-count">{archived.length}</span>}
            </button>
          </div>
          <button type="button" className="btn btn-primary project-list-create" onClick={() => setShowCreateModal(true)}>
            <Plus size={18} /> New project
          </button>
        </div>
      </div>

      {view === 'archived' && loadingArchived ? (
        <div className="loading-screen" style={{ minHeight: 'auto', padding: '60px 0' }}>
          <div className="loading-spinner"></div>
        </div>
      ) : displayed.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            {view === 'archived' ? <Archive size={64} /> : <FolderKanban size={64} />}
          </div>
          <h2>{view === 'archived' ? 'No archived projects' : 'No projects yet'}</h2>
          <p>
            {view === 'archived'
              ? 'Archived projects will appear here. Use the settings menu inside a project to archive it.'
              : 'Create your first project to start organizing your work'}
          </p>
        </div>
      ) : (
        <div className="projects-grid">
          {displayed.map(project => (
            <Link
              key={project._id}
              to={`/project/${project._id}`}
              className="project-card"
            >
              <div 
                className={`project-card-header ${project.image ? 'has-image' : ''}`}
                style={{ 
                  ...(project.image ? {
                    backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${absoluteMediaUrl(project.image)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  } : {
                    background: project.color || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  })
                }}
              >
                <FolderKanban size={32} color="white" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
              </div>
              
              <div className="project-card-body">
                <h3>
                  {project.name}
                  {project.type === 'design-workflow' && (
                    <span className="project-type-pill" title="Design Assurance project">
                      <Shield size={10} /> DA
                    </span>
                  )}
                </h3>
                {project.description && (
                  <p className="project-description">{project.description}</p>
                )}
                
                <div className="project-meta">
                  <div className="project-members">
                    {project.members && project.members.length > 0 ? (
                      <>
                        <Users size={14} />
                        <span>{project.members.length} member{project.members.length !== 1 ? 's' : ''}</span>
                      </>
                    ) : (
                      <>
                        <Users size={14} />
                        <span>No members</span>
                      </>
                    )}
                  </div>
                  
                  <div className="project-date">
                    {format(new Date(project.createdAt), 'MMM d, yyyy')}
                  </div>
                </div>

                {project.members && project.members.length > 0 && (
                  <div className="avatar-group" style={{ marginTop: '16px' }}>
                    {project.members.slice(0, 5).map((member, idx) => (
                      <UserAvatar
                        key={member._id}
                        user={member}
                        className="avatar avatar-sm"
                        title={member.name}
                        style={{
                          width: '32px',
                          height: '32px',
                          fontSize: '12px',
                          border: '2px solid white',
                          zIndex: project.members.length - idx
                        }}
                      />
                    ))}
                    {project.members.length > 5 && (
                      <div 
                        className="avatar avatar-sm"
                        style={{
                          width: '32px',
                          height: '32px',
                          fontSize: '12px',
                          border: '2px solid white',
                          background: 'var(--gray-400)'
                        }}
                      >
                        +{project.members.length - 5}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
      {showCreateModal && (
        <CreateProjectModal onClose={() => setShowCreateModal(false)} onCreate={onCreateProject} />
      )}
    </div>
  );
}

export default ProjectList;
